import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import { ChevronRight, FileText } from '@lifeomic/chromicons';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { getTestProps } from '../../util/testProps';
import {
  countResourcesToReview,
  resourceTypeCounts,
} from './memberExtractionResourceGroups';
import {
  heroChipStyle,
  heroChipTone,
  heroSpace,
  heroSurface,
} from './memberExtractionTheme';
import type { MemberExtractionDocumentGroup } from './useMemberExtractionDocuments';

const messages = defineMessages({
  heading: {
    id: 'phc-web-toolkit.memberExtractions.list.heading',
    defaultMessage: 'Extracted documents',
  },
  documentCount: {
    id: 'phc-web-toolkit.memberExtractions.list.documentCount',
    defaultMessage: '{count, plural, one {# document} other {# documents}}',
  },
  toReview: {
    id: 'phc-web-toolkit.memberExtractions.list.toReview',
    defaultMessage: '{count} to review',
  },
  reviewed: {
    id: 'phc-web-toolkit.memberExtractions.list.reviewed',
    defaultMessage: 'Reviewed',
  },
});

const useStyles = makeStyles(theme => ({
  card: {
    margin: 0,
    padding: heroSpace.tight,
    listStyle: 'none',
    backgroundColor: heroSurface.background,
    borderRadius: heroSurface.radiusLarge,
    boxShadow: heroSurface.shadowSmall,
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: heroSpace.regular,
    padding: heroSpace.tight,
    borderRadius: heroSurface.radiusMedium,
    color: 'inherit',
    textDecoration: 'none',
    transition: `background-color ${heroSurface.transition}`,
    '&:hover': {
      backgroundColor: heroSurface.muted,
    },
    '&:hover $chevron': {
      color: theme.palette.primary.main,
      transform: 'translateX(2px)',
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 auto',
    width: 40,
    height: 40,
    borderRadius: heroSurface.radiusMedium,
    backgroundColor: heroSurface.muted,
    color: theme.palette.primary.main,
  },
  chevron: {
    flex: '0 0 auto',
    width: 18,
    height: 18,
    color: heroSurface.textMuted,
    transition: `transform ${heroSurface.transition}, color ${heroSurface.transition}`,
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  },
}));

const pageStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: heroSpace.regular,
};

const headerStyle = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: heroSpace.regular,
  padding: `0 ${heroSpace.regular}px`,
};

const textColumnStyle = {
  display: 'flex',
  flexDirection: 'column' as const,
  flex: '1 1 auto',
  minWidth: 0,
  gap: heroSpace.tight,
};

const titleStyle = {
  margin: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
};

const chipRowStyle = {
  display: 'flex',
  flexWrap: 'wrap' as const,
  gap: heroSpace.tight,
};

const dotStyle = {
  width: 8,
  height: 8,
  borderRadius: '50%',
};

const dividerStyle = {
  height: 1,
  margin: `0 ${heroSpace.tight}px`,
  backgroundColor: heroSurface.muted,
};

type MemberExtractionDocumentListProps = {
  groups: MemberExtractionDocumentGroup[];
  basePath: string;
};

export const MemberExtractionDocumentList: React.FC<
  MemberExtractionDocumentListProps
> = ({ groups, basePath }) => {
  const { formatMessage } = useIntl();
  const classes = useStyles();

  return (
    <div style={pageStyle}>
      <div style={headerStyle}>
        <Text size="subbody" weight="bold" style={{ margin: 0 }}>
          {formatMessage(messages.heading)}
        </Text>
        <Text
          size="caption"
          style={{ margin: 0, color: heroSurface.textMuted }}
        >
          {formatMessage(messages.documentCount, { count: groups.length })}
        </Text>
      </div>
      <ul
        className={classes.card}
        {...getTestProps('member-extraction-documents')}
      >
        {groups.map((group, index) => {
          const toReview = countResourcesToReview(group.resources);
          return (
            <li key={group.id} {...getTestProps('member-extraction-document')}>
              {index > 0 ? <div aria-hidden style={dividerStyle} /> : null}
              <Link
                to={`${basePath}/${encodeURIComponent(group.id)}`}
                className={classes.item}
              >
                <span aria-hidden className={classes.icon}>
                  <FileText width={20} height={20} />
                </span>
                <span style={textColumnStyle}>
                  <Text size="subbody" weight="bold" style={titleStyle}>
                    {group.title}
                  </Text>
                  <span style={chipRowStyle}>
                    {resourceTypeCounts(group.resources, formatMessage).map(
                      part => (
                        <span
                          key={part.key}
                          style={{ ...heroChipStyle, ...heroChipTone.neutral }}
                        >
                          <span
                            aria-hidden
                            style={{ ...dotStyle, backgroundColor: part.color }}
                          />
                          {`${part.count} ${part.label}`}
                        </span>
                      ),
                    )}
                  </span>
                </span>
                <span
                  style={{
                    ...heroChipStyle,
                    ...(toReview > 0
                      ? heroChipTone.warning
                      : heroChipTone.success),
                  }}
                >
                  {toReview > 0
                    ? formatMessage(messages.toReview, { count: toReview })
                    : formatMessage(messages.reviewed)}
                </span>
                <ChevronRight aria-hidden className={classes.chevron} />
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
